import { useDemoFetcherWithSwr } from "@/api";
import { userAtom } from "@/atoms/userAtom";
import { SpotCard } from "@/common/SpotCard";
import { InteractionItem } from "@/generatedApi/userUserInteractionLoggerApi";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { notFoundSpotIdsAtom } from "@/atoms/notFoundSpotIdsAtom";

export interface UserInteractionSpotCardProps {
  interactionItem: InteractionItem;
  leftSwipeAction?: "bookmark" | "flag" | "delete";
  rightSwipeAction?: "bookmark" | "flag" | "delete";
  forceShowSwipeBars?: boolean;
  onLeftTap?: () => void;
  onRightTap?: () => void;
  isProcessing?: boolean;
  onDislikeClick?: () => void;
  isDisliked?: boolean;
}

export const UserInteractionSpotCard = ({
  interactionItem,
  leftSwipeAction,
  rightSwipeAction,
  forceShowSwipeBars = false,
  onLeftTap,
  onRightTap,
  isProcessing,
  onDislikeClick,
  isDisliked
}: UserInteractionSpotCardProps) => {
  const location = useLocation();
  const setNotFoundSpotIds = useSetAtom(notFoundSpotIdsAtom);
  const user = useAtomValue(userAtom);
  const hideSwipeBars = forceShowSwipeBars
    ? false
    : location.pathname === "/saved" || location.pathname === "/visited";

  const { data, isLoading, error } = useDemoFetcherWithSwr({
    apiName: "spot_provider",
    serviceName: "default",
    operationId: "getPublicSpotItem",
    requestParameters: {
      spotId: interactionItem.spot_id
    }
  });

  // 404エラーを検出したらatomに記録
  useEffect(() => {
    if (error?.status === 404 || error?.statusCode === 404) {
      setNotFoundSpotIds((prev: Set<string>) => {
        const newSet = new Set(prev);
        newSet.add(interactionItem.spot_id);
        return newSet;
      });
    }
  }, [error, interactionItem.spot_id, setNotFoundSpotIds]);

  // 404エラーの場合はカードを非表示
  if (error?.status === 404 || error?.statusCode === 404) {
    return null;
  }

  return (
    <SpotCard
      spotId={interactionItem.spot_id}
      name={data?.spot_item?.name ?? ""}
      googleCid={data?.spot_item?.google_cid ?? ""}
      isLoading={isLoading}
      error={error}
      isLoaded={!isLoading && !error}
      hideSwipeBars={hideSwipeBars}
      leftSwipeAction={leftSwipeAction}
      rightSwipeAction={rightSwipeAction}
      onLeftTap={onLeftTap}
      onRightTap={onRightTap}
      isProcessing={isProcessing}
      onDislikeClick={onDislikeClick}
      isDisliked={isDisliked}
      meta={`登録日時: ${new Date(interactionItem.timestamp_created).toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      })}`}
      recommendationId={interactionItem.recommendation_id ?? undefined}
      trackingUserId={user?.userId}
    />
  );
};
